import React, { useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import AppContext from 'Utils/AppContext';
import { INK, STATUS } from 'Utils/styleTokens';
import { encodePrefill } from '../SensitiveDataLandscape/UnknownSources/managerSources';
import InstanceTable from './InstanceTable';
import {
  buildCreateSourcePrefill,
  coveringSource,
  relatedFolderMatches,
} from './relatedFolderMatches';
import { RISK_BG_COLORS, RISK_TEXT_COLORS } from './constants';

function formatRows(rowCount) {
  if (rowCount == null) return '—';
  const n = Number(rowCount);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function bytesToGB(bytes) {
  if (bytes == null) return null;
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round((n / (1024 * 1024 * 1024)) * 100) / 100;
}

function formatCreated(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

// Maps the enriched identity members from the overview response into the row shape
// InstanceTable already renders. Counts come from the platform-api enrichment of the
// worker's dataSourceMembers entries; older worker builds without those fields fall
// back to dashes. Region info isn't yet exposed by the meta-analysis pipeline.
function adaptSources(dataSources) {
  if (!Array.isArray(dataSources)) return [];
  return dataSources.map((ds) => ({
    dataSourceId: ds.dataSourceId,
    name: ds.name || `Data source ${ds.dataSourceId}`,
    database: ds.database || null,
    region: '—',
    type: shortType(ds.engine || ds.datasourceType),
    server: ds.host && ds.port ? `${ds.host}:${ds.port}` : (ds.host || ''),
    sizeGB: bytesToGB(ds.totalSizeBytes),
    rows: formatRows(ds.totalRowCount),
    tables: ds.tableCount != null ? ds.tableCount : null,
    // Real schema similarity of this source to the cluster representative (0.0–1.0 from the
    // backend), rendered as a percentage. Falls back to 100 for the representative / exact
    // twins that carry no explicit edge; null → shown as "—".
    match: ds.similarityToRepresentative != null
      ? Math.round(Number(ds.similarityToRepresentative) * 100)
      : null,
    created: formatCreated(ds.latestSnapshotAt),
  }));
}

function shortType(engineOrType) {
  if (!engineOrType) return 'file';
  const t = String(engineOrType).toUpperCase();
  if (t === 'POSTGRESQL' || t === 'POSTGRES' || t === 'PG') return 'pg';
  if (t === 'MYSQL' || t === 'MARIADB') return 'mysql';
  if (t === 'MONGODB' || t === 'MONGO' || t === 'NOSQL') return 'mongo';
  if (t.startsWith('FILE_')) {
    if (t.includes('DELIMITED') || t.includes('CSV') || t.includes('TSV')) return 'csv';
    return 'file';
  }
  return 'file';
}

const SchemaIdentityDetail = ({ identity, identities }) => {
  const history = useHistory();
  const { connections } = useContext(AppContext);

  // Folders elsewhere in the estate that matched this identity's commercial product
  // (worker-side folder attribution on file-source profile snapshots). Rendered as
  // extra rows in the InstanceTable — the folder belongs in the identity's own list,
  // faded when no data source is rooted at it yet.
  const folderMatches = useMemo(() => {
    if (!identity) return [];
    return relatedFolderMatches(identity, identities || []).map((m) => ({
      ...m,
      matchPct: Math.round((m.confidence || 0) * 100),
      covered: coveringSource(m.folderPath, connections),
    }));
  }, [identity, identities, connections]);

  const handleCreateSource = (m) =>
    history.push(
      `/sources/source-editor?prefill=${encodePrefill(buildCreateSourcePrefill(m, connections))}`
    );

  if (!identity) {
    return (
      <div className='metadata-right'>
        <div className='mr-header'>
          <span className='mr-name'>Schema Intelligence</span>
        </div>
        <div className='mr-body'>
          <div className='metadata-empty'>Select a schema identity from the list</div>
        </div>
      </div>
    );
  }

  const sources = adaptSources(identity.dataSources);
  const cm = identity.commercialMatch;
  // Identified commercial product ("SugarCRM Community") — shown per row in the
  // instance table's Region/Commercial-schema column.
  const commercialSchemaName = cm
    ? [cm.vendorName, cm.productName].filter(Boolean).join(' ') || null
    : null;

  return (
    <div className='metadata-right'>
      <div className='mr-header'>
        <span className='mr-name'>{identity.name}</span>
        <span className='mr-meta'>
          <span
            className='metadata-rbadge'
            style={{
              background: RISK_BG_COLORS[identity.risk],
              color: RISK_TEXT_COLORS[identity.risk],
            }}
          >
            {identity.risk} risk
          </span>
          <span style={{ opacity: 0.5 }}>·</span>
          <span>{identity.tables} tables</span>
          <span style={{ opacity: 0.5 }}>·</span>
          <span>since {identity.origin}</span>
        </span>
      </div>
      <div className='mr-body'>
        {cm && (
          <div
            style={{
              padding: '8px 12px',
              background: STATUS.success.bg,
              border: '1px solid rgba(22, 163, 74, 0.35)',
              borderRadius: 4,
              margin: '8px 0',
              fontSize: 12,
            }}
          >
            <strong>{cm.vendorName} {cm.productName}</strong>
            {' · '}
            <span style={{ color: STATUS.success.text }}>
              {Math.round((cm.confidence || 0) * 100)}% match ({cm.confidenceLabel})
            </span>
            {cm.regulatoryFlags && cm.regulatoryFlags.length > 0 && (
              <span style={{ color: INK[500], marginLeft: 8 }}>
                Flags: {cm.regulatoryFlags.join(', ')}
              </span>
            )}
            <span
              role='link'
              tabIndex={0}
              data-testid='view-in-library'
              onClick={() => history.push(
                cm.schemaId != null
                  ? `/metadata/library?highlight=${encodeURIComponent(cm.schemaId)}`
                  : '/metadata/library'
              )}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.click(); }}
              style={{
                marginLeft: 8, color: STATUS.success.text, cursor: 'pointer',
                textDecoration: 'underline', fontWeight: 600,
              }}
            >
              View in Schema Library →
            </span>
          </div>
        )}
        <InstanceTable
          sources={sources}
          folderMatches={folderMatches}
          onCreateSource={handleCreateSource}
          commercialSchemaName={commercialSchemaName}
        />
      </div>
    </div>
  );
};

export default SchemaIdentityDetail;
